<script lang="ts">
export default {
  name: 'MonthPicker',
}
</script>

<script lang="ts" setup>
import { computed } from 'vue'
import moment from 'moment'
import { KkSelect } from '../../base'
import type { Moment } from 'moment'

export interface Props {
  start: Moment | null
  end: Moment | null
  min: Moment
  max: Moment
}

export interface Emits {
  (key: 'update:start', value: Props['start']): void
  (key: 'update:end', value: Props['end']): void
  (key: 'change'): void
}

const props = defineProps<Props>()

const emits = defineEmits<Emits>()

const modelStart = computed({
  get: () => props.start,
  set: (value) => emits('update:start', value),
})

const startYearValue = computed(() => modelStart.value ? `${modelStart.value.get('year')}年` : '')
const startMonthValue = computed(() => modelStart.value ? `${String(modelStart.value.get('month') + 1).padStart(2, '0')}月` : '')

const modelEnd = computed({
  get: () => props.end,
  set: (value) => emits('update:end', value),
})

const endYearValue = computed(() => modelEnd.value ? `${modelEnd.value.get('year')}年` : '')
const endMonthValue = computed(() => modelEnd.value ? `${String(modelEnd.value.get('month') + 1).padStart(2, '0')}月` : '')

// MEMO: min、max、endの指定状況から開始月の年の選択肢を形成する
const startYearOptions = computed(() => {
  const min = props.min ? props.min.get('year') : moment().subtract(9, 'years').get('year')
  const max = props.max ? props.max.get('year') : moment().get('year')
  const values = []

  for (let i = min; i <= max; i++) {
    values.push(i)
  }

  return values.map<string>((value) => `${value}年`)
})

// MEMO: min、max、endの指定状況から開始月の月の選択肢を形成する
const startMonthOptions = computed(() => {
  const values = []
  const base = modelStart.value || moment()

  let min = 1
  if (base.isSame(props.min, 'year')) min = props.min.get('month') + 1

  let max = 12
  if (base.isSame(modelEnd.value, 'year') && modelEnd.value) max = modelEnd.value.get('month') + 1
  if (base.isSame(props.max, 'year')) max = props.max.get('month') + 1

  for (let i = min; i <= max; i++) values.push(i)

  return values.map<string>((value) => `${String(value).padStart(2, '0')}月`)
})

const handleUpdateStartYear = (newValue: string) => {
  const newValueNumber = Number(newValue.replace(/(\d{4})年/, '$1'))
  const value = (modelStart.value ? moment(modelStart.value) : moment()).set('year', newValueNumber)

  if (value.isBefore(props.min)) {
    // MEMO: 選択した結果、minより前の年月になった場合はminの年月に合わせる
    modelStart.value = value.set('month', props.min.get('month'))
  } else if (value.isAfter(props.max)) {
    // MEMO: 選択した結果、maxより後の年月になった場合はmaxの年月に合わせる
    modelStart.value = value.set('month', props.max.get('month'))
  } else {
    modelStart.value = value
  }

  // MEMO: endがstartを超える場合はstartに合わせる
  if (modelEnd.value && modelStart.value.isAfter(modelEnd.value)) {
    modelEnd.value = moment(value)
  }

  // MEMO: endがstartより前の年月になった場合はstartに合わせる
  if (modelEnd.value && modelEnd.value.isBefore(value)) {
    modelEnd.value = moment(value)
  }

  emits('change')
}

const handleUpdateStartMonth = (newValue: string) => {
  const value = Number(newValue.replace(/(\d{2})月/, '$1'))
  modelStart.value = (modelStart.value ? moment(modelStart.value) : moment()).set('month', value - 1)
  emits('change')
}

// MEMO: min、max、startの指定状況から終了月の年の選択肢を形成する
const endYearOptions = computed(() => {
  const min = modelStart.value ? modelStart.value.get('year') : props.min ? props.min.get('year') : moment().subtract(9, 'years').get('year')
  const max = props.max ? props.max.get('year') : moment().get('year')
  const values = []

  for (let i = min; i <= max; i++) {
    values.push(i)
  }

  return values.map<string>((value) => `${value}年`)
})

// MEMO: min、max、startの指定状況から終了月の月の選択肢を形成する
const endMonthOptions = computed(() => {
  const values = []
  const base = modelEnd.value || moment()

  let min = 1
  if (modelStart.value && base.isSame(modelStart.value, 'year')) {
    min = modelStart.value.get('month') + 1
  } else if (base.isSame(props.min, 'year')) {
    min = props.min.get('month') + 1
  }

  let max = 12
  if (base.isSame(props.max, 'year')) max = props.max.get('month') + 1

  for (let i = min; i <= max; i++) values.push(i)

  return values.map<string>((value) => `${String(value).padStart(2, '0')}月`)
})

const handleUpdateEndYear = (newValue: string) => {
  const newValueNumber = Number(newValue.replace(/(\d{4})年/, '$1'))
  const value = (modelEnd.value ? moment(modelEnd.value) : moment()).set('year', newValueNumber)

  if (value.isBefore(props.min)) {
    // MEMO: 選択した結果、minより前の年月になった場合はminの年月に合わせる
    modelEnd.value = value.set('month', props.min.get('month'))
  } else if (value.isAfter(props.max)) {
    // MEMO: 選択した結果、maxより後の年月になった場合はmaxの年月に合わせる
    modelEnd.value = value.set('month', props.max.get('month'))
  } else if (modelStart.value && value.isBefore(modelStart.value)) {
    modelEnd.value = value.set('month', modelStart.value.get('month') + 1)
  } else {
    modelEnd.value = value
  }

  emits('change')
}

const handleUpdateEndMonth = (newValue: string) => {
  const value = Number(newValue.replace(/(\d{2})月/, '$1'))
  modelEnd.value = (modelEnd.value ? moment(modelEnd.value) : moment()).set('month', value - 1)
  emits('change')
}
</script>

<template>
  <div :class="$style.container">
    <fieldset :class="$style.fields">
      <label :class="$style.label">{{ $t('labels.termSelect.startMonth') }}</label>
      <div :class="$style.content" class="content">
        <KkSelect :model-value="startYearValue" :options="startYearOptions" name="start-year" placeholder="" @update:model-value="handleUpdateStartYear" />
        <KkSelect :model-value="startMonthValue" :options="startMonthOptions" name="start-month" placeholder="" @update:model-value="handleUpdateStartMonth" />
      </div>
    </fieldset>

    <fieldset :class="$style.fields">
      <label :class="$style.label">{{ $t('labels.termSelect.endMonth') }}</label>
      <div :class="$style.content" class="content">
        <KkSelect :model-value="endYearValue" :options="endYearOptions" name="end-year" placeholder="" @update:model-value="handleUpdateEndYear" />
        <KkSelect :model-value="endMonthValue" :options="endMonthOptions" name="end-month" placeholder="" @update:model-value="handleUpdateEndMonth" />
      </div>
    </fieldset>
  </div>
</template>

<style lang="scss" module>
@import '../../../../../stylesheets/biz_spa/foundation/var';
@import '../../../../../stylesheets/biz_spa/foundation/mixin';

.container {
  padding: 16px;

  @include mq {
    padding: 32px;
  }
}

.fields {
  display: block;

  &:not(:first-child) {
    margin-top: 24px;
  }
}

.label {
  display: inline-block;
  font-size: 12px;
  font-weight: bold;
  line-height: 1;
  color: $color-font-base;
}

.content {
  display: flex;
  align-items: center;
  gap: 0 16px;
  margin-top: 8px;
}
</style>

<style lang="scss" scoped>
.content :deep(.c-select) {
  margin-top: 0 !important;
}
</style>
